// src/components/common/DataTable.jsx
import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
    Search,
    ChevronUp,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    Loader2,
    X
} from 'lucide-react';

const getColumnSearchText = (row, col) => {
    if (typeof col.getFilterValue === 'function') {
        return String(col.getFilterValue(row) ?? '');
    }
    if (typeof col.render === 'function') {
        try {
            const rendered = col.render(row);
            if (typeof rendered === 'string' || typeof rendered === 'number') {
                return String(rendered);
            }
        } catch {
            /* ignore render errors during filter */
        }
    }
    if (col.accessor && row[col.accessor] !== undefined && row[col.accessor] !== null) {
        const value = row[col.accessor];
        if (typeof value === 'boolean') return value ? 'Aktif' : 'Pasif';
        return String(value);
    }
    return '';
};

const isColumnFilterable = (col) => {
    if (col.filterable === false) return false;
    if (!col.accessor || col.accessor === 'actions') return false;
    return true;
};

/**
 * Genel yeniden kullanılabilir DataTable komponenti.
 * Üst arama, kolon bazlı filtre, sıralama ve sayfalandırma.
 * Server-side modda arama/filtre sırasında input odağını (focus) korur.
 */
const DataTable = ({
    data = [],
    columns = [],
    itemsPerPage = 20,
    pageSizeOptions = [10, 20, 50, 100],
    showSearch = true,
    searchPlaceholder = "Tüm sütunlarda ara...",
    showColumnFilters = true,
    showPagination = true,
    emptyMessage = "Gösterilecek veri bulunamadı.",
    isLoading = false,
    errorMessage = null,
    actionsHeader = null,
    getRowClassName = () => '',
    serverSide = false,
    totalRows = 0,
    onServerParamsChange = null
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [columnFilters, setColumnFilters] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(itemsPerPage);
    const [sortColumn, setSortColumn] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc');

    useEffect(() => {
        setPageSize(itemsPerPage);
    }, [itemsPerPage]);

    const filterableAccessors = useMemo(
        () => columns.filter(isColumnFilterable).map(col => col.accessor),
        [columns]
    );

    useEffect(() => {
        setColumnFilters(prev => {
            const next = {};
            let changed = false;
            filterableAccessors.forEach(accessor => {
                if (prev[accessor]) next[accessor] = prev[accessor];
            });
            if (Object.keys(prev).length !== Object.keys(next).length) changed = true;
            return changed ? next : prev;
        });
    }, [filterableAccessors]);

    // Callback ref so changing parent inline function reference doesn't re-trigger debounce effect unnecessarily
    const onServerParamsChangeRef = useRef(onServerParamsChange);
    useEffect(() => {
        onServerParamsChangeRef.current = onServerParamsChange;
    }, [onServerParamsChange]);

    useEffect(() => {
        if (serverSide && onServerParamsChangeRef.current) {
            const timeoutId = setTimeout(() => {
                onServerParamsChangeRef.current({
                    page: currentPage,
                    itemsPerPage: pageSize,
                    searchTerm,
                    columnFilters,
                    sortColumn,
                    sortDirection
                });
            }, 280);
            return () => clearTimeout(timeoutId);
        }
    }, [currentPage, pageSize, searchTerm, columnFilters, sortColumn, sortDirection, serverSide]);

    const handleSearchChange = (value) => {
        setSearchTerm(value);
        setCurrentPage(1);
    };

    const handleSort = (columnAccessor) => {
        if (!columnAccessor) return;
        if (sortColumn === columnAccessor) {
            setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortColumn(columnAccessor);
            setSortDirection('asc');
        }
        setCurrentPage(1);
    };

    const handleColumnFilterChange = (accessor, value) => {
        setColumnFilters(prev => {
            const next = { ...prev };
            if (!value) delete next[accessor];
            else next[accessor] = value;
            return next;
        });
        setCurrentPage(1);
    };

    const clearAllFilters = () => {
        setSearchTerm('');
        setColumnFilters({});
        setCurrentPage(1);
    };

    const processedData = useMemo(() => {
        if (serverSide) return data || [];

        let currentFilteredData = data || [];

        if (showSearch && searchTerm) {
            const lowercasedSearchTerm = String(searchTerm).trim().toLocaleLowerCase('tr-TR');
            currentFilteredData = currentFilteredData.filter(row =>
                columns.some(col => {
                    if (!col.searchable && !isColumnFilterable(col)) return false;
                    return getColumnSearchText(row, col).toLocaleLowerCase('tr-TR').includes(lowercasedSearchTerm);
                })
            );
        }

        const activeColumnFilters = Object.entries(columnFilters).filter(([, value]) => String(value).trim());
        if (showColumnFilters && activeColumnFilters.length > 0) {
            currentFilteredData = currentFilteredData.filter(row =>
                activeColumnFilters.every(([accessor, value]) => {
                    const col = columns.find(c => c.accessor === accessor);
                    if (!col) return true;
                    return getColumnSearchText(row, col)
                        .toLocaleLowerCase('tr-TR')
                        .includes(String(value).trim().toLocaleLowerCase('tr-TR'));
                })
            );
        }

        if (sortColumn) {
            currentFilteredData = [...currentFilteredData].sort((a, b) => {
                const aValue = a[sortColumn];
                const bValue = b[sortColumn];

                if (aValue === null || aValue === undefined) return sortDirection === 'asc' ? 1 : -1;
                if (bValue === null || bValue === undefined) return sortDirection === 'asc' ? -1 : 1;

                if (typeof aValue === 'string' && typeof bValue === 'string') {
                    return sortDirection === 'asc'
                        ? aValue.localeCompare(bValue, 'tr')
                        : bValue.localeCompare(aValue, 'tr');
                }

                if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
                return 0;
            });
        }

        return currentFilteredData;
    }, [data, columns, showSearch, searchTerm, showColumnFilters, columnFilters, sortColumn, sortDirection, serverSide]);

    const effectiveTotalRows = serverSide ? totalRows : processedData.length;
    const totalPages = Math.max(1, Math.ceil(effectiveTotalRows / pageSize));

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [totalPages, currentPage]);

    const paginatedData = useMemo(() => {
        if (serverSide) return processedData;
        const startIndex = (currentPage - 1) * pageSize;
        return processedData.slice(startIndex, startIndex + pageSize);
    }, [processedData, currentPage, pageSize, serverSide]);

    const hasActiveColumnFilters = Object.values(columnFilters).some(v => String(v).trim());
    const startRecord = effectiveTotalRows === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const endRecord = Math.min(currentPage * pageSize, effectiveTotalRows);

    return (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200/70 dark:border-gray-700/70 p-5">
            {(showSearch || actionsHeader || hasActiveColumnFilters) && (
                <div className="flex flex-col md:flex-row justify-between items-stretch md:items-center gap-3 mb-4">
                    {showSearch && (
                        <div className="relative w-full md:w-96">
                            <input
                                type="text"
                                placeholder={searchPlaceholder}
                                className="w-full pl-10 pr-9 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
                                value={searchTerm}
                                onChange={(e) => handleSearchChange(e.target.value)}
                            />
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-300" size={17} />
                            {searchTerm && (
                                <button
                                    type="button"
                                    onClick={() => handleSearchChange('')}
                                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                                    title="Aramayı temizle"
                                >
                                    <X size={15} />
                                </button>
                            )}
                        </div>
                    )}

                    <div className="flex items-center gap-2 ml-auto">
                        {(searchTerm || hasActiveColumnFilters) && (
                            <button
                                type="button"
                                onClick={clearAllFilters}
                                className="px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg flex items-center gap-1 transition-colors"
                            >
                                <X size={14} /> Tablo Filtrelerini Temizle
                            </button>
                        )}
                        {actionsHeader && <div>{actionsHeader}</div>}
                    </div>
                </div>
            )}

            {errorMessage && (
                <div className="bg-red-100 dark:bg-red-900/60 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-100 p-3.5 rounded-lg mb-4 text-sm flex items-center">
                    <span className="font-semibold mr-2">Hata:</span> {errorMessage}
                </div>
            )}

            <div className="relative overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                {isLoading && paginatedData.length > 0 && (
                    <div className="absolute inset-0 bg-white/50 dark:bg-gray-900/50 backdrop-blur-[1px] z-10 flex items-center justify-center">
                        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700">
                            <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                            <span className="text-xs font-medium text-gray-700 dark:text-gray-200">Yükleniyor...</span>
                        </div>
                    </div>
                )}

                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700/80">
                        <tr>
                            {columns.map((col, index) => (
                                <th
                                    key={`h-${col.accessor || index}`}
                                    className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300 select-none ${
                                        col.sortable ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600/70' : ''
                                    }`}
                                    style={col.width ? { width: col.width } : undefined}
                                    onClick={() => col.sortable && handleSort(col.accessor)}
                                >
                                    <div className="flex items-center gap-1">
                                        <span>{col.header}</span>
                                        {col.sortable && (
                                            <span className="inline-flex flex-col">
                                                {sortColumn === col.accessor && sortDirection === 'asc' && <ChevronUp size={15} className="text-blue-500" />}
                                                {sortColumn === col.accessor && sortDirection === 'desc' && <ChevronDown size={15} className="text-blue-500" />}
                                                {sortColumn !== col.accessor && <ChevronUp size={14} className="text-gray-400 opacity-40" />}
                                            </span>
                                        )}
                                    </div>
                                </th>
                            ))}
                        </tr>
                        {showColumnFilters && (
                            <tr className="bg-gray-50/90 dark:bg-gray-700/50 border-t border-gray-200/60 dark:border-gray-600/60">
                                {columns.map((col, index) => (
                                    <th
                                        key={`f-${col.accessor || index}`}
                                        className="px-3 pb-2.5 pt-1.5"
                                        style={col.width ? { width: col.width } : undefined}
                                    >
                                        {isColumnFilterable(col) ? (
                                            <input
                                                type="text"
                                                value={columnFilters[col.accessor] || ''}
                                                onChange={(e) => handleColumnFilterChange(col.accessor, e.target.value)}
                                                onClick={(e) => e.stopPropagation()}
                                                placeholder={`${col.header}...`}
                                                className="w-full px-2.5 py-1 text-xs font-normal border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                            />
                                        ) : null}
                                    </th>
                                ))}
                            </tr>
                        )}
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                        {isLoading && paginatedData.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length} className="py-12 text-center">
                                    <div className="inline-flex items-center justify-center gap-2.5 text-gray-600 dark:text-gray-300">
                                        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                                        <span className="text-sm font-medium">Veriler yükleniyor...</span>
                                    </div>
                                </td>
                            </tr>
                        ) : paginatedData.length > 0 ? (
                            paginatedData.map((row, index) => (
                                <tr
                                    key={row.id ?? `row-${index}`}
                                    className={`hover:bg-gray-50/90 dark:hover:bg-gray-700/60 transition-colors ${getRowClassName(row, index)}`}
                                >
                                    {columns.map((col, colIndex) => (
                                        <td
                                            key={colIndex}
                                            className={`px-4 py-3 text-sm whitespace-nowrap text-gray-900 dark:text-gray-100 ${col.cellClassName || ''}`}
                                            style={col.width ? { width: col.width } : undefined}
                                        >
                                            {col.render ? col.render(row) : (row[col.accessor] ?? '-')}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={columns.length} className="py-10 px-4 text-center text-sm text-gray-500 dark:text-gray-400">
                                    {(searchTerm || hasActiveColumnFilters)
                                        ? 'Arama veya kolon filtrelerine uygun kayıt bulunamadı.'
                                        : emptyMessage}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {showPagination && (
                <div className="flex flex-col sm:flex-row justify-between items-center gap-3 mt-4 pt-2 text-sm text-gray-600 dark:text-gray-300">
                    <div className="flex flex-wrap items-center gap-3">
                        <span>
                            Toplam <strong className="font-semibold text-gray-900 dark:text-white">{effectiveTotalRows.toLocaleString('tr-TR')}</strong> kayıt
                            {effectiveTotalRows > 0 && (
                                <span className="text-gray-500 dark:text-gray-400 ml-1">
                                    ({startRecord.toLocaleString('tr-TR')} – {endRecord.toLocaleString('tr-TR')} arası gösteriliyor)
                                </span>
                            )}
                        </span>
                        {pageSizeOptions && pageSizeOptions.length > 1 && (
                            <div className="flex items-center gap-1.5">
                                <span className="text-xs text-gray-500 dark:text-gray-400">Sayfa başına:</span>
                                <select
                                    value={pageSize}
                                    onChange={(e) => {
                                        setPageSize(Number(e.target.value));
                                        setCurrentPage(1);
                                    }}
                                    className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                >
                                    {pageSizeOptions.map(size => (
                                        <option key={size} value={size}>{size}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>

                    {totalPages > 1 && (
                        <div className="flex items-center gap-1.5">
                            <button
                                type="button"
                                onClick={() => setCurrentPage(1)}
                                disabled={currentPage === 1}
                                title="İlk Sayfa"
                                className="p-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                <ChevronsLeft size={16} />
                            </button>
                            <button
                                type="button"
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed flex items-center text-xs font-medium"
                            >
                                <ChevronLeft size={15} className="mr-0.5" /> Önceki
                            </button>
                            <span className="px-3 py-1 text-xs font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-md border border-blue-200/60 dark:border-blue-800/60">
                                Sayfa {currentPage} / {totalPages.toLocaleString('tr-TR')}
                            </span>
                            <button
                                type="button"
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed flex items-center text-xs font-medium"
                            >
                                Sonraki <ChevronRight size={15} className="ml-0.5" />
                            </button>
                            <button
                                type="button"
                                onClick={() => setCurrentPage(totalPages)}
                                disabled={currentPage === totalPages}
                                title="Son Sayfa"
                                className="p-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                <ChevronsRight size={16} />
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default DataTable;
